import axios from "axios";
import { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import Select from "react-select";
import CustomDateFilterModal from "../../components/CustomDateFilterModal";
import Loader from "../../components/Loader";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import { BASE_URL } from "../../utils/Element";
import MetricBox from "./Component/MetricBox";
import ScoreCircle from "./Component/ScoreCircle";

const ScoreCard = () => {
  const [config] = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [timeRange, setTimeRange] = useState("Quarterly");
  const [scoreCard, setScoreCard] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [isSwitchingUser, setIsSwitchingUser] = useState(false);
  const [bdsOptions, setBdsOptions] = useState([]);
  const [customDates, setCustomDates] = useState({ startDate: "", endDate: "" });

  /* =========================
     ✅ HELPERS
  ========================= */

  const normalizeNumber = (value) => {
    if (value == null) return NaN;
    if (typeof value === "number") return value;
    const cleaned = value.toString().replace(/,/g, "");
    return Number(cleaned);
  };

  const isTimeString = (value) =>
    typeof value === "string" && value.includes(":");

  const timeToSeconds = (time) => {
    if (!time || typeof time !== "string") return NaN;
    const clean = time.replace(/\s/g, "");
    const parts = clean.split(":").map(Number);
    if (parts.some(isNaN)) return NaN;

    const [h = 0, m = 0, s = 0] = parts;
    return h * 3600 + m * 60 + s;
  };

  const getColor = (actual, goal) => {
    if (actual == null || goal == null) return "gray";

    if (isTimeString(actual) && isTimeString(goal)) {
      const a = timeToSeconds(actual);
      const g = timeToSeconds(goal);
      if (Number.isNaN(a) || Number.isNaN(g)) return "gray";
      if (a > g) return "green";
      if (a === g) return "gray";
      return "red";
    }

    const a = normalizeNumber(actual);
    const g = normalizeNumber(goal);

    if (Number.isNaN(a) || Number.isNaN(g)) return "gray";
    if (a > g) return "green";
    if (a === g) return "gray";
    return "red";
  };

  /* =========================
     ✅ API CALLS
  ========================= */

  const fetchScoreCard = () => {
    if (timeRange === "Custom" && (!customDates.startDate || !customDates.endDate)) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    let url = `${BASE_URL}/goals?active_tab=${activeTab}&time_range=${timeRange}`;

    // Only send show_user_id if a real user is selected (not Team Dashboard)
    if (selectedUser?.value && selectedUser.value !== "team") {
      url += `&show_user_id=${selectedUser.value}`;
    }

    if (timeRange === "Custom") {
      url += `&start_date=${customDates.startDate}&end_date=${customDates.endDate}`;
    }

    axios
      .get(url, config)
      .then((res) => setScoreCard(res?.data?.data || []))
      .catch((error) => {
        if (error.response?.data?.message && error.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(error.response?.data?.message);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const fetchUsers = () => {
    setIsLoading(true);

    axios
      .get(`${BASE_URL}/get-datalisting?list=users&role_id=3`, config)
      .then((res) => {
        const userOptions =
          res?.data?.data?.map((el) => ({
            value: el.id,
            label: `${el.first_name} ${el.last_name}`,
            role: el.role_id,
            isUser: true,
          })) || [];

        // Add "Team Dashboard" as the first option
        const allOptions = [
          {
            value: "team",
            label: "Team Dashboard",
            isTeam: true,
          },
          ...userOptions,
        ];

        setBdsOptions(allOptions);

        // Pre-select "Team Dashboard" by default
        setSelectedUser(allOptions[0]);
      })
      .catch((err) => {

        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setIsLoading(false));
  };

  /* =========================
     ✅ EFFECTS
  ========================= */

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchScoreCard();
  }, [selectedUser, activeTab, timeRange, customDates]);

  /* =========================
     ✅ HANDLERS
  ========================= */

  const handleUserSwitch = (option) => {
    setSelectedUser(option);
  };

  const handleTimeRangeChange = (e) => {
    const value = e.target.value;
    setTimeRange(value);

    if (value === "Custom") {
      setShowCustomDateModal(true);
    } else {
      setCustomDates({ startDate: "", endDate: "" });
    }
  };

  const handleCustomFilterSuccess = () => {
    setShowCustomDateModal(false);
  };

  const handleModalClose = () => {
    setShowCustomDateModal(false);
    setCustomDates({ startDate: "", endDate: "" });
    setTimeRange("Quarterly");
  };

  /* =========================
     ✅ UI
  ========================= */

  return (
    <BaseLayout>
      <div className="contact-header mb-4">
        <p className="head-1 dark-H">Scorecard</p>
      </div>

      {/* Tabs */}
      <div className="task-tabs mx-1 mt-2 flex justify-between body-L dark-M mb-4">
        <div className="flex gap-4">
          {["My Scorecard Metrics", "Team Scorecard Metrics"].map((tab, index) => (
            <p
              key={index}
              role="button"
              className={`py-1 cursor-pointer ${
                activeTab === index
                  ? "head-4 dark-H border-b-2 border-gray-700"
                  : "text-gray-500"
              }`}
              onClick={() => setActiveTab(index)}
            >
              {tab}
            </p>
          ))}
        </div>

        {/* User / Team Select Dropdown - Only show on Team Tab */}
        {activeTab === 1 && (
          <div className="relative ml-2">
            <Select
              className="body-S min-w-[200px]"
              placeholder="Select User or Team"
              value={selectedUser}
              options={bdsOptions}
              onChange={handleUserSwitch}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              isClearable={false}
            />
          </div>
        )}
      </div>

      {(activeTab === 0 || activeTab === 1) && (
        <div className="flex justify-between items-stretch gap-6 relative min-h-[400px]">
          {isLoading && (
            <div className="absolute inset-0 z-10 flex justify-center items-center bg-white/40 backdrop-blur-[1px] rounded-lg">
              <Loader />
            </div>
          )}

          <div className="flex-1 flex flex-col justify-between space-y-8">
                <div>
                  <h2 className="head-2 dark-H mb-2">LEAD GENERATION</h2>
                  <div className="bg-white p-6 rounded-lg shadow">
                    <div className="grid grid-cols-4 gap-6 justify-items-center">
                      <ScoreCircle
                        label="Dials"
                        actual={scoreCard?.dial}
                        goal={scoreCard?.goal?.dials_goal}
                        color={getColor(scoreCard?.dial, scoreCard?.goal?.dials_goal)}
                      />
                      <ScoreCircle
                        label="Conversations"
                        actual={scoreCard?.conversation}
                        goal={scoreCard?.goal?.conversations_goal}
                        color={getColor(scoreCard?.conversation, scoreCard?.goal?.conversations_goal)}
                      />
                      <ScoreCircle
                        label="Talk Time"
                        actual={scoreCard?.talk_time}
                        goal={scoreCard?.goal?.talk_time_goal}
                        color={getColor(scoreCard?.talk_time, scoreCard?.goal?.talk_time_goal)}
                      />
                      <ScoreCircle
                        label="New 8x8s"
                        actual={scoreCard?.new_8x8}
                        goal={scoreCard?.goal?.new_8x8s_goal}
                        color={getColor(scoreCard?.new_8x8, scoreCard?.goal?.new_8x8s_goal)}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between gap-6 flex-1">
                  <div className="w-1/3 flex flex-col">
                    <h2 className="head-2 dark-H mb-3 uppercase">Buyer Progress</h2>
                    <div className="flex-1 bg-white p-6 rounded-lg shadow flex flex-col justify-center">
                      <div className="flex flex-col items-center gap-6">
                        <ScoreCircle
                          label="New Buyer Leads"
                          actual={scoreCard?.new_buyer_lead}
                          goal={scoreCard?.goal?.new_buyer_leads_goal}
                          color={getColor(scoreCard?.new_buyer_lead, scoreCard?.goal?.new_buyer_leads_goal)}
                        />
                        <ScoreCircle
                          label="Initial Offers w/ Different Buyers"
                          actual={scoreCard?.initial_offers_different_buyers}
                          goal={scoreCard?.goal?.initial_offers_w_different_buyers_goal}
                          color={getColor(
                            scoreCard?.initial_offers_different_buyers,
                            scoreCard?.goal?.initial_offers_w_different_buyers_goal
                          )}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="w-2/3 flex flex-col">
                    <h2 className="head-2 dark-H mb-3 uppercase">Seller Progress</h2>
                    <div className="flex-1 bg-white p-6 rounded-lg shadow flex flex-col justify-center">
                      <div className="grid grid-cols-2 gap-6 place-items-center">
                        <ScoreCircle
                          label="New Seller Leads"
                          actual={scoreCard?.new_seller_lead}
                          goal={scoreCard?.goal?.new_seller_leads_goal}
                          color={getColor(scoreCard?.new_seller_lead, scoreCard?.goal?.new_seller_leads_goal)}
                        />
                        <ScoreCircle
                          label="New BOVs"
                          actual={scoreCard?.new_bov}
                          goal={scoreCard?.goal?.new_bovs_goal}
                          color={getColor(scoreCard?.new_bov, scoreCard?.goal?.new_bovs_goal)}
                        />
                        <ScoreCircle
                          label="Initial Offers w/ Different Sellers"
                          actual={scoreCard?.initial_offers_different_seller}
                          goal={scoreCard?.goal?.initial_offers_w_different_sellers_goal}
                          color={getColor(
                            scoreCard?.initial_offers_different_seller,
                            scoreCard?.goal?.initial_offers_w_different_sellers_goal
                          )}
                        />
                        <ScoreCircle
                          label={<>Exclusive<br />Listings</>}
                          actual={scoreCard?.exclusive_listing}
                          goal={scoreCard?.goal?.exclusive_listings_goal}
                          color={getColor(scoreCard?.exclusive_listing, scoreCard?.goal?.exclusive_listings_goal)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="w-64 flex flex-col justify-between">
                <div className="flex justify-end mb-3 items-center">
                  <select
                    value={timeRange}
                    onChange={handleTimeRangeChange}
                    className="border border-gray-300 rounded-md px-3 py-1 body-S dark-M focus:outline-none"
                  >
                    <option>Today</option>
                    <option>Weekly</option>
                    <option>Monthly</option>
                    <option>Quarterly</option>
                    <option>Yearly</option>
                    <option>Custom</option>
                  </select>

                  {timeRange === "Custom" && (
                    <button
                      onClick={() => setShowCustomDateModal(true)}
                      className="ml-2 border border-gray-300 px-2 py-1 rounded-md body-S dark-M"
                    >
                      Edit Dates
                    </button>
                  )}
                </div>

                <div className="flex flex-col gap-4 flex-1 justify-around">
                  <MetricBox
                    title="Executed LOI"
                    actual={scoreCard?.executed_loi}
                    goal={scoreCard?.goal?.executed_loi_goal}
                    color={getColor(scoreCard?.executed_loi, scoreCard?.goal?.executed_loi_goal)}
                  />
                  <MetricBox
                    title="Gross Hopper Gains"
                    actual={scoreCard?.gross_hopping_gain}
                    goal={scoreCard?.goal?.gross_hopper_gains_goal}
                    color={getColor(scoreCard?.gross_hopping_gain, scoreCard?.goal?.gross_hopper_gains_goal)}
                  />
                  <MetricBox
                    title="Gross Commission Closed"
                    actual={scoreCard?.gross_hopping_cross}
                    goal={scoreCard?.goal?.gross_commission_closed_goal}
                    color={getColor(scoreCard?.gross_hopping_cross, scoreCard?.goal?.gross_commission_closed_goal)}
                  />
                </div>
              </div>
        </div>
      )}

      <CustomDateFilterModal
        showModal={showCustomDateModal}
        onClose={handleModalClose}
        filterCustomDate={customDates}
        onSetFilterCustomDate={setCustomDates}
        onSuccess={handleCustomFilterSuccess}
      />
    </BaseLayout>
  );
};

export default ScoreCard;